/**
 * ----------------------------------------------------------------------------
 * SharedFlowContextProvider
 * ----------------------------------------------------------------------------
 *
 * Supplies context to logic flows code that is used in all environments.
 */

import * as React from 'react';
import noop from 'lodash/noop';
import {weakMemo} from '@packages/utilities/memo';
import type {Secrets, ComponentForm} from '@packages/systems/flows/types';
import Reselect from 'reselect';
import type {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import type {
  CMSItemsQueryOptions,
  GetCollectionItemsResponse,
} from '@packages/systems/cms/types';
import type {UseQueryResult} from '@tanstack/react-query';
import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {type PageState} from '@packages/systems/core/records/PageRecord';

// Below eslint disable is needed to import these types
/* eslint-disable webflow/no-dangerous-imports */
import type {
  GetUsersQueryParams,
  GetUsersResult,
} from '@packages/systems/users/usersManagement';

type UseUsersObject = {
  siteShortName: string;
  query: GetUsersQueryParams;
  debounceMs: number;
};
type UseUsersPrefetch =
  | undefined
  | number
  | {
      forward: number;
      backward: number;
    };

type DesignerAndPageStores = {
  PageStore: PageState;
  DesignerStore: DesignerStateType;
};
type SelectLogicFormFields = Reselect.Selector<
  DesignerAndPageStores,
  Record<string, ComponentForm>
>;

type SharedFlowContextType = {
  openCMSPanel: () => void;
  useCMSBlock: () => {
    hasDependencies: boolean;
    collectionName: string;
  };
  selectLogicFormFields: SelectLogicFormFields;
  credentials: Secrets;
  useCollectionItemsPage: (
    collection: CollectionRecordType,
    opts: CMSItemsQueryOptions
  ) => UseQueryResult<GetCollectionItemsResponse>;
  useUsers: (
    arg1: UseUsersObject,
    arg2: UseUsersPrefetch
  ) => UseQueryResult<GetUsersResult>;
};

const defaultContextValue = {
  openCMSPanel: noop,
  useCMSBlock: noop,
  selectLogicFormFields: noop,
  credentials: [],
  useCollectionItemsPage: noop,
  useUsers: noop,
} as const;

export const SharedFlowContext =
  // @ts-expect-error - TS2345 - Argument of type '{ readonly openCMSPanel: (...args: any[]) => void; readonly useCMSBlock: (...args: any[]) => void; readonly selectLogicFormFields: (...args: any[]) => void; readonly credentials: readonly []; readonly useCollectionItemsPage: (...args: any[]) => void; readonly useUsers: (...args: any[]) => void; }' is not assignable to parameter of type 'SharedFlowContextType'.
  React.createContext<SharedFlowContextType>(defaultContextValue);
SharedFlowContext.displayName = 'SharedFlowContext';

export const useSharedFlowContext = () => React.useContext(SharedFlowContext);

// @ts-expect-error - TS7006 - Parameter 'contextValue' implicitly has an 'any' type.
const getContextValue = weakMemo((contextValue) =>
  // @ts-expect-error - TS7006 - Parameter 'useCMSBlock' implicitly has an 'any' type.
  weakMemo((useCMSBlock) =>
    // @ts-expect-error - TS7006 - Parameter 'selectLogicFormFields' implicitly has an 'any' type.
    weakMemo((selectLogicFormFields) =>
      // @ts-expect-error - TS7006 - Parameter 'credentials' implicitly has an 'any' type.
      weakMemo((credentials) =>
        // @ts-expect-error - TS7006 - Parameter 'useCollectionItemsPage' implicitly has an 'any' type.
        weakMemo((useCollectionItemsPage) =>
          // @ts-expect-error - TS7006 - Parameter 'useUsers' implicitly has an 'any' type.
          weakMemo((useUsers) => ({
            ...contextValue,
            useCMSBlock,
            selectLogicFormFields,
            credentials,
            useCollectionItemsPage,
            useUsers,
          }))
        )
      )
    )
  )
);

type Props = {
  children: React.ReactNode;
  useCMSBlock: () => {
    hasDependencies: boolean;
    collectionName: string;
  };
  selectLogicFormFields: SelectLogicFormFields;
  openCMSPanel: () => void;
  credentials: Secrets;
  useCollectionItemsPage: (
    collection: CollectionRecordType,
    opts: CMSItemsQueryOptions
  ) => UseQueryResult<GetCollectionItemsResponse>;
  useUsers: (
    arg1: UseUsersObject,
    arg2: UseUsersPrefetch
  ) => UseQueryResult<GetUsersResult>;
};

export class SharedFlowContextProvider extends React.PureComponent<Props> {
  contextValue = {
    useCMSBlock: this.props.useCMSBlock,
    openCMSPanel: this.props.openCMSPanel,
    credentials: this.props.credentials,
    useCollectionItemsPage: this.props.useCollectionItemsPage,
    useUsers: this.props.useUsers,
  };

  render() {
    const {
      useCMSBlock,
      selectLogicFormFields,
      credentials,
      useCollectionItemsPage,
      useUsers,
    } = this.props;

    return (
      <SharedFlowContext.Provider
        value={getContextValue(this.contextValue)(useCMSBlock)(
          selectLogicFormFields
        )(credentials)(useCollectionItemsPage)(useUsers)}
      >
        {this.props.children}
      </SharedFlowContext.Provider>
    );
  }
}
